import React, { useState, useMemo } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Loader2, CheckCircle2, AlertCircle, Check, X, ArrowLeft, Eye, EyeOff, User, Info } from 'lucide-react';

interface SignUpFormProps {
  onSuccess?: () => void;
}

interface ValidateEmailResponse {
  is_tekion_user: boolean;
  has_account: boolean;
  display_name: string;
}

// Password requirement interface
interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

// Password requirements configuration
const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'length',
    label: 'At least 8 characters',
    validator: (password: string) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter (A-Z)',
    validator: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter (a-z)',
    validator: (password: string) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    label: 'One number (0-9)',
    validator: (password: string) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'One special character (!@#$%^&*...)',
    validator: (password: string) => /[^A-Za-z0-9]/.test(password),
  },
];

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  
  // Step state: 0 = email, 1 = password
  const [step, setStep] = useState(0);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // User info from email validation
  const [displayName, setDisplayName] = useState('');
  
  // Loading and state management
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Check which requirements are met
  const requirementStatus = useMemo(() => {
    return PASSWORD_REQUIREMENTS.map((req) => ({
      ...req,
      met: req.validator(password),
    }));
  }, [password]);

  // Check if all requirements are met
  const allRequirementsMet = useMemo(() => {
    return requirementStatus.every((req) => req.met);
  }, [requirementStatus]);

  // Calculate password strength (0-100)
  const passwordStrength = useMemo(() => {
    const metCount = requirementStatus.filter((req) => req.met).length;
    return (metCount / PASSWORD_REQUIREMENTS.length) * 100;
  }, [requirementStatus]);

  // Get strength label and color
  const strengthInfo = useMemo(() => {
    if (password.length === 0) return { label: '', color: 'bg-gray-200' };
    if (passwordStrength < 40) return { label: 'Weak', color: 'bg-red-500' };
    if (passwordStrength < 80) return { label: 'Medium', color: 'bg-yellow-500' };
    return { label: 'Strong', color: 'bg-green-500' };
  }, [password, passwordStrength]);

  const validateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setValidatingEmail(true);
    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002';
      const normalizedBase = backendUrl.replace(/\/+$/, '');
      
      const response = await fetch(`${normalizedBase}/auth/validate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Not a Tekion user
        throw new Error(data.detail || 'This email is not in Tekion DMS, please ask your manager for help.');
      }

      const validationResult = data as ValidateEmailResponse;
      
      if (validationResult.has_account) {
        // Tekion user with existing account - show info message (they can click the Log in button below)
        setInfoMessage('An account already exists for this email. Please log in instead.');
        return;
      }

      // No account - proceed to password step
      setDisplayName(validationResult.display_name);
      setStep(1);
      
    } catch (error) {
      console.error('Email validation failed:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to validate email. Please try again.');
      }
    } finally {
      setValidatingEmail(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Validate all password requirements
    if (!allRequirementsMet) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSigningUp(true);
    try {
      await signUp(email, password);
      setSuccess(true);
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Don't auto-redirect - user needs to confirm email first
    } catch (error) {
      console.error('Sign up failed:', error);
      let errorMessage = 'Sign up failed. Please check your information.';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        errorMessage = JSON.stringify(error);
      } else {
        errorMessage = String(error);
      }
      setError(errorMessage);
    } finally {
      setSigningUp(false);
    }
  };

  const handleBack = () => {
    setStep(0);
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordRequirements(false);
  };

  const isLoading = validatingEmail || signingUp;

  // Success state
  if (success) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Check Your Email</CardTitle>
          <CardDescription className="text-center">
            We've sent a confirmation link to <strong>{email}</strong>. 
            Please check your inbox and click the link to activate your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 text-blue-700 text-sm p-4 rounded-md border border-blue-200">
            <p className="font-medium mb-2">What to do next:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Check your email inbox</li>
              <li>Click the confirmation link</li>
              <li>Return here to log in</li>
            </ol>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Didn't receive the email? Check your spam folder.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">
          {step === 0 ? 'Create Account' : `Welcome${displayName ? `, ${displayName.split(' ')[0]}` : ''}!`}
        </CardTitle>
        <CardDescription>
          {step === 0 
            ? 'Enter your Tekion email to get started' 
            : 'Create a secure password for your account'
          }
        </CardDescription>
        
        {/* Step indicator */}
        <div className="flex gap-2 justify-center pt-2">
          <div className={`w-2 h-2 rounded-full transition-colors ${step === 0 ? 'bg-primary' : 'bg-gray-300'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${step === 1 ? 'bg-primary' : 'bg-gray-300'}`} />
        </div>
      </CardHeader>
      
      {/* Sliding container - only hide horizontal overflow for slide animation */}
      <div className="overflow-x-hidden">
        <div 
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${step * 100}%)` }}
        >
          {/* Step 1: Email */}
          <div className="w-full flex-shrink-0">
            <form onSubmit={validateEmail}>
              <CardContent className="space-y-4">
                {/* Error message */}
                {error && step === 0 && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-md border border-red-200 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                
                {/* Info message */}
                {infoMessage && (
                  <div className="bg-blue-50 text-blue-700 text-sm p-3 rounded-md border border-blue-200 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>{infoMessage}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                      if (infoMessage) setInfoMessage(null);
                    }}
                    disabled={isLoading}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                  {/* <p className="text-xs text-muted-foreground">
                    Only Tekion DMS users can create an account
                  </p> */}
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 mt-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {validatingEmail ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
                
                {/* Or divider */}
                <div className="relative flex items-center w-full">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="px-3 text-xs text-muted-foreground uppercase">or</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full border border-primary text-primary hover:bg-primary/10"
                  onClick={() => navigate('/login')}
                  disabled={isLoading}
                >
                  Log in
                </Button>
              </CardFooter>
            </form>
          </div>
          
          {/* Step 2: Password */}
          <div className="w-full flex-shrink-0">
            <form onSubmit={handleSignUp}>
              <CardContent className="space-y-4">
                {error && step === 1 && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-md border border-red-200 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                
                {/* Email display */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{email}</p>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      Change email
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(null);
                      }}
                      onFocus={() => setShowPasswordRequirements(true)}
                      disabled={isLoading}
                      required
                      autoComplete="new-password"
                      autoFocus={step === 1}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  
                  {/* Password strength bar */}
                  {password.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Password strength</span>
                        <span className={`font-medium ${
                          strengthInfo.label === 'Weak' ? 'text-red-600' :
                          strengthInfo.label === 'Medium' ? 'text-yellow-600' :
                          strengthInfo.label === 'Strong' ? 'text-green-600' : ''
                        }`}>
                          {strengthInfo.label}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${strengthInfo.color}`}
                          style={{ width: `${passwordStrength}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Password requirements checklist */}
                  {showPasswordRequirements && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                      <ul className="space-y-1">
                        {requirementStatus.map((req) => (
                          <li 
                            key={req.id}
                            className={`flex items-center gap-2 text-xs ${
                              req.met ? 'text-green-600' : 'text-gray-500'
                            }`}
                          >
                            {req.met ? (
                              <Check className="h-3.5 w-3.5" />
                            ) : (
                              <X className="h-3.5 w-3.5" />
                            )}
                            <span>{req.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (error) setError(null);
                      }}
                      disabled={isLoading}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Password match indicator */}
                  {confirmPassword.length > 0 && (
                    <div className={`flex items-center gap-1 text-xs ${
                      password === confirmPassword ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {password === confirmPassword ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          <span>Passwords match</span>
                        </>
                      ) : (
                        <>
                          <X className="h-3.5 w-3.5" />
                          <span>Passwords do not match</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-3 mt-4">
                <div className="flex gap-2 w-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={isLoading}
                    className="px-3"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={isLoading || !allRequirementsMet || password !== confirmPassword}
                  >
                    {signingUp ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </CardFooter>
            </form>
          </div>
        </div>
      </div>
    </Card>
  );
};
